import React, { useState } from "react";
import { useHistory } from "react-router-dom";
import {
  Box,
  Stack,
  Typography,
  Chip,
  Button,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Alert,
} from "@mui/material";
import { AdminPanelSettings, DevicesOutlined, ArrowBack } from "@mui/icons-material";
import { useAdminRequests } from "../../hooks/useAdminRequests";
import { ORANGE, humanizeError } from "../../components/ui";

/** Admin queue: review, approve, and reject enrollment requests (spec §16.9). */
export default function RegistrationRequestsPage() {
  const history = useHistory();
  const { list, approve, reject } = useAdminRequests();
  const [rejectTarget, setRejectTarget] = useState(null);
  const [reason, setReason] = useState("");

  const requests = Array.isArray(list.data) ? list.data : [];
  const pending = requests.filter((r) => r.status === "pending");

  const submitReject = () => {
    reject.mutate({ id: rejectTarget.id, reason: reason.trim() });
    setRejectTarget(null);
    setReason("");
  };

  return (
    <Box sx={{ maxWidth: 900, mx: "auto", p: { xs: 2, md: 4 } }}>
      <Stack direction="row" alignItems="center" gap={1.5} sx={{ mb: 0.5 }}>
        <Tooltip title="Back">
          <IconButton
            onClick={() => history.goBack()}
            size="small"
            sx={{ border: "1px solid var(--border-1)", color: "var(--text-1)", flexShrink: 0 }}
          >
            <ArrowBack fontSize="small" />
          </IconButton>
        </Tooltip>
        <AdminPanelSettings sx={{ color: ORANGE }} />
        <Typography sx={{ fontSize: 22, fontWeight: 700, color: "var(--text-1)" }}>
          Browser enrollment requests
        </Typography>
      </Stack>
      <Typography sx={{ fontSize: 14, color: "var(--text-2)", mb: 3 }}>
        Approve a browser to let the user complete enrollment, or reject with a reason.
      </Typography>

      {list.isLoading && (
        <Stack alignItems="center" py={4}>
          <CircularProgress sx={{ color: ORANGE }} />
        </Stack>
      )}
      {list.isError && (
        <Alert severity="error" sx={{ borderRadius: "10px" }}>
          {humanizeError(list.error)}
        </Alert>
      )}
      {!list.isLoading && pending.length === 0 && (
        <Typography sx={{ color: "var(--text-2)", fontSize: 14 }}>No pending requests.</Typography>
      )}

      <Stack gap={1.5}>
        {pending.map((r) => {
          const id = r.id;
          return (
            <Stack
              key={id}
              direction={{ xs: "column", md: "row" }}
              alignItems={{ md: "center" }}
              justifyContent="space-between"
              gap={1.5}
              sx={{
                p: 2,
                borderRadius: "12px",
                backgroundColor: "var(--surface-1)",
                border: "1px solid var(--border-1)",
              }}
            >
              <Stack gap={0.25}>
                <Stack direction="row" alignItems="center" gap={1}>
                  <Typography sx={{ fontSize: 15, fontWeight: 600, color: "var(--text-1)" }}>
                    {r.analyst_username || `Analyst #${r.analyst_id}` || "Unknown user"}
                  </Typography>
                  <Chip size="small" label={r.label || "This browser"} sx={{ height: 20, fontSize: 11 }} />
                  {r.device_type && (
                    <Chip
                      size="small"
                      variant="outlined"
                      label={r.device_type}
                      sx={{ height: 20, fontSize: 11, textTransform: "capitalize" }}
                    />
                  )}
                </Stack>
                <Typography sx={{ fontSize: 12, color: "var(--text-2)", maxWidth: 520 }} noWrap>
                  {r.user_agent || "—"}
                </Typography>
                <Typography sx={{ fontSize: 12, color: "var(--text-2)" }}>
                  {r.ip ? `${r.ip} · ` : ""}
                  {fmt(r.requested_at)}
                </Typography>
              </Stack>
              <Stack direction="row" gap={1}>
                <Button
                  variant="contained"
                  size="small"
                  disabled={approve.isLoading}
                  onClick={() => approve.mutate(id)}
                  sx={{ textTransform: "none", backgroundColor: ORANGE, "&:hover": { backgroundColor: "rgba(200,93,0,1)" } }}
                >
                  Approve
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  color="error"
                  onClick={() => setRejectTarget(r)}
                  sx={{ textTransform: "none" }}
                >
                  Reject
                </Button>
                {r.analyst_id != null && (
                  <Button
                    variant="text"
                    size="small"
                    startIcon={<DevicesOutlined sx={{ fontSize: 16 }} />}
                    onClick={() => history.push(`/admin/webauthn/credentials?analyst=${r.analyst_id}`)}
                    sx={{ textTransform: "none", color: "var(--text-2)" }}
                  >
                    Manage devices
                  </Button>
                )}
              </Stack>
            </Stack>
          );
        })}
      </Stack>

      <Dialog open={!!rejectTarget} onClose={() => setRejectTarget(null)}>
        <DialogTitle>Reject request</DialogTitle>
        <DialogContent sx={{ minWidth: 360 }}>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={2}
            size="small"
            placeholder="Reason (shown to the user)"
            sx={{ mt: 1 }}
            value={reason}
            onChange={(e) => setReason(e.target.value.slice(0, 280))}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectTarget(null)} sx={{ textTransform: "none" }}>
            Cancel
          </Button>
          <Button onClick={submitReject} disabled={!reason.trim()} color="error" sx={{ textTransform: "none" }}>
            Reject
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

function fmt(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString();
  } catch (e) {
    return iso;
  }
}
